import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { formatDateTime } from '@formosa/shared/utils/formatDateTime';
import { journeyApi } from '../../api/journeyApi';
import { journeySituationApi } from '../../api/journeySituationApi';
import { useAuth } from '../../auth/AuthContext';
import useMemberDisplayNames from '../member-display/useMemberDisplayNames';
import { collectMemberIds } from '../member-display/memberDisplayModel';
import { WORKSPACE_PRODUCT_CHROME } from '../journey-workspace/journeyWorkspacePresentation';
import {
  classifyJourneyAccessError,
  normalizeJourneyWorkspace,
} from '../journey-workspace/journeyWorkspaceModel';
import JourneyWorkspaceNav from '../journey-workspace/JourneyWorkspaceNav';
import {
  SITUATION_SEVERITIES,
  activeCollaboratorOptions,
  assigneeLabel,
  buildAssignSituationBody,
  buildCancelSituationBody,
  buildResolveSituationBody,
  buildUpdateSituationBody,
  canMutateSituation,
  classifySituationError,
  normalizeSituation,
  situationErrorMessage,
} from './journeySituationModel';
import '../journey-workspace/journey-workspace.css';
import './journey-situation.css';

export default function JourneySituationPage() {
  const { journeyId, situationId } = useParams();
  const { user } = useAuth();
  const viewerMemberId = user?.memberId ?? user?.id;
  const generation = useRef(0);
  const mutating = useRef(false);
  const titleId = useId();
  const descriptionId = useId();
  const severityId = useId();
  const assigneeId = useId();
  const resolveNoteId = useId();
  const cancelReasonId = useId();

  const [phase, setPhase] = useState('loading');
  const [error, setError] = useState('');
  const [journey, setJourney] = useState(null);
  const [situation, setSituation] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ title: '', description: '', severity: 'MEDIUM' });
  const [assignee, setAssignee] = useState('');
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState('');
  const [confirmResolve, setConfirmResolve] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [resolutionNote, setResolutionNote] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [needsReload, setNeedsReload] = useState(false);
  const memberSummaries = useMemberDisplayNames(collectMemberIds([
    ...(journey?.members || []).map((member) => member.memberId),
    situation?.assignedToMemberId,
    situation?.createdByMemberId,
    situation?.resolvedByMemberId,
    situation?.cancelledByMemberId,
  ]));

  const load = useCallback(async ({ soft = false } = {}) => {
    const requestId = ++generation.current;
    const scopedJourney = String(journeyId || '');
    const scopedSituation = String(situationId || '');
    if (!soft) {
      setPhase('loading');
      setError('');
      setEditing(false);
      setConfirmResolve(false);
      setConfirmCancel(false);
    }
    try {
      const [detailPayload, situationPayload] = await Promise.all([
        journeyApi.getDetail(scopedJourney),
        journeySituationApi.get(scopedJourney, scopedSituation),
      ]);
      if (requestId !== generation.current) return;
      if (String(journeyId) !== scopedJourney || String(situationId) !== scopedSituation) return;
      const normalizedJourney = normalizeJourneyWorkspace(detailPayload, viewerMemberId);
      const normalized = normalizeSituation(situationPayload);
      setJourney(normalizedJourney);
      setSituation(normalized);
      setDraft({
        title: normalized.title,
        description: normalized.description,
        severity: normalized.severity || 'MEDIUM',
      });
      setAssignee(normalized.assignedToMemberId);
      setNeedsReload(false);
      setPhase('ready');
    } catch (cause) {
      if (requestId !== generation.current) return;
      const kind = classifySituationError(cause);
      const access = classifyJourneyAccessError(cause);
      if (kind === 'not-found' || access === 'not-found') setPhase('not-found');
      else if (kind === 'forbidden' || access === 'forbidden') setPhase('forbidden');
      else {
        setError(situationErrorMessage(cause, '旅程狀況無法載入。'));
        setPhase('error');
      }
    }
  }, [journeyId, situationId, viewerMemberId]);

  useEffect(() => {
    if (!/^[1-9]\d*$/.test(journeyId || '') || !/^[1-9]\d*$/.test(situationId || '')) {
      setPhase('not-found');
      return undefined;
    }
    load();
    return () => { generation.current += 1; };
  }, [journeyId, situationId, load]);

  async function runMutation(task) {
    if (!situation || mutating.current) return false;
    mutating.current = true;
    setBusy(true);
    setFormError('');
    try {
      await task();
      await load({ soft: true });
      return true;
    } catch (cause) {
      const kind = classifySituationError(cause);
      if (kind === 'conflict') {
        setNeedsReload(true);
        setFormError(situationErrorMessage(cause));
      } else {
        setFormError(situationErrorMessage(cause));
      }
      return false;
    } finally {
      mutating.current = false;
      setBusy(false);
    }
  }

  const canMutate = canMutateSituation({
    role: journey?.requesterRole,
    lifecycleStatus: journey?.lifecycleStatus,
    situationStatus: situation?.situationStatus,
    createdByMemberId: situation?.createdByMemberId,
    viewerMemberId,
  });

  if (phase === 'loading') {
    return (
      <main className="journey-workspace-state" aria-live="polite">
        <p>載入狀況…</p>
      </main>
    );
  }
  if (phase === 'not-found') {
    return (
      <main className="journey-workspace-state">
        <h1>找不到這個狀況</h1>
        <p>這個狀況無法查看，或已經移除。</p>
        <Link to={`/journeys/${journeyId}/situations`}>返回狀況</Link>
      </main>
    );
  }
  if (phase === 'forbidden') {
    return (
      <main className="journey-workspace-state">
        <h1>{WORKSPACE_PRODUCT_CHROME.forbiddenTitle}</h1>
        <p>{WORKSPACE_PRODUCT_CHROME.forbiddenBody}</p>
        <Link to="/journeys">{WORKSPACE_PRODUCT_CHROME.backToMyJourneys}</Link>
      </main>
    );
  }
  if (phase === 'error') {
    return (
      <main className="journey-workspace-state" role="alert">
        <h1>無法開啟這個狀況</h1>
        <p>{error}</p>
        <button type="button" onClick={() => load()}>{WORKSPACE_PRODUCT_CHROME.tryAgain}</button>
      </main>
    );
  }

  const collaborators = activeCollaboratorOptions(journey?.members, memberSummaries);

  return (
    <main className="journey-workspace">
      <header className="journey-workspace__header">
        <div>
          <p className="journey-workspace__eyebrow">旅程狀況</p>
          <h1 className="journey-situation__title">{situation.title}</h1>
          <p>
            {situation.situationStatusLabel}
            <span className="visually-hidden"> ({situation.situationStatus})</span>
          </p>
        </div>
        <Link className="journey-workspace__back" to={`/journeys/${journey.journeyId}/situations`}>
          返回列表
        </Link>
      </header>
      <JourneyWorkspaceNav journeyId={journey.journeyId} />

      <section className="journey-workspace__panel journey-situation" aria-labelledby="situation-detail-heading">
        <h2 id="situation-detail-heading" className="visually-hidden">狀況詳情</h2>
        {formError ? <p className="journey-workspace__error" role="alert">{formError}</p> : null}
        {needsReload ? (
          <button type="button" onClick={() => load()} disabled={busy}>重新載入目前狀態</button>
        ) : null}

        <dl className="journey-situation__facts">
          <div>
            <dt>situationId</dt>
            <dd className="journey-situation__wrap">{situation.situationId}</dd>
          </div>
          <div>
            <dt>situationType</dt>
            <dd>{situation.situationTypeLabel} ({situation.situationType})</dd>
          </div>
          <div>
            <dt>severity</dt>
            <dd>{situation.severityLabel} ({situation.severity})</dd>
          </div>
          <div>
            <dt>assignee</dt>
            <dd>{situation.assignedToMemberId ? assigneeLabel(situation.assignedToMemberId, memberSummaries) : '未指派'}</dd>
          </div>
          <div>
            <dt>detectedAt</dt>
            <dd>{formatDateTime(situation.detectedAt)}</dd>
          </div>
          <div>
            <dt>updatedAt</dt>
            <dd>{formatDateTime(situation.updateTime)}</dd>
          </div>
          <div>
            <dt>rowVersion</dt>
            <dd>{situation.rowVersion}</dd>
          </div>
        </dl>

        {situation.description ? <p className="journey-situation__wrap">{situation.description}</p> : null}
        {situation.resolvedAt ? (
          <p>
            resolvedAt {formatDateTime(situation.resolvedAt)}
            {situation.resolutionNote ? ` · ${situation.resolutionNote}` : ''}
          </p>
        ) : null}
        {situation.cancelledAt ? (
          <p>
            cancelledAt {formatDateTime(situation.cancelledAt)}
            {situation.cancellationReason ? ` · ${situation.cancellationReason}` : ''}
          </p>
        ) : null}

        {canMutate && !editing ? (
          <div className="journey-situation__actions">
            <button type="button" disabled={busy} onClick={() => setEditing(true)}>編輯</button>
            <button
              type="button"
              className="journey-situation__primary"
              disabled={busy}
              onClick={() => { setConfirmResolve(true); setConfirmCancel(false); }}
            >
              標記為已處理
            </button>
            <button
              type="button"
              className="journey-situation__danger"
              disabled={busy}
              onClick={() => { setConfirmCancel(true); setConfirmResolve(false); }}
            >
              取消此狀況
            </button>
          </div>
        ) : null}

        {canMutate && editing ? (
          <form
            className="journey-situation__edit"
            onSubmit={(event) => {
              event.preventDefault();
              runMutation(() => journeySituationApi.update(
                journey.journeyId,
                situation.situationId,
                buildUpdateSituationBody(draft, situation.rowVersion),
              )).then((ok) => { if (ok) setEditing(false); });
            }}
          >
            <label htmlFor={titleId}>
              標題
              <input
                id={titleId}
                value={draft.title}
                maxLength={100}
                disabled={busy}
                onChange={(event) => setDraft((current) => ({ ...current, title: event.target.value }))}
              />
            </label>
            <label htmlFor={severityId}>
              severity
              <select
                id={severityId}
                value={draft.severity}
                disabled={busy}
                onChange={(event) => setDraft((current) => ({ ...current, severity: event.target.value }))}
              >
                {SITUATION_SEVERITIES.map((severity) => (
                  <option key={severity} value={severity}>{severity}</option>
                ))}
              </select>
            </label>
            <label htmlFor={descriptionId}>
              description
              <textarea
                id={descriptionId}
                maxLength={1000}
                value={draft.description}
                disabled={busy}
                onChange={(event) => setDraft((current) => ({ ...current, description: event.target.value }))}
              />
            </label>
            <div className="journey-situation__actions">
              <button type="submit" className="journey-situation__primary" disabled={busy}>
                {busy ? '儲存中…' : '儲存'}
              </button>
              <button type="button" disabled={busy} onClick={() => setEditing(false)}>取消編輯</button>
            </div>
          </form>
        ) : null}

        {canMutate ? (
          <form
            className="journey-situation__edit"
            onSubmit={(event) => {
              event.preventDefault();
              runMutation(() => journeySituationApi.assign(
                journey.journeyId,
                situation.situationId,
                buildAssignSituationBody(assignee, situation.rowVersion),
              ));
            }}
          >
            <label htmlFor={assigneeId}>
              assignedToMemberId
              <select
                id={assigneeId}
                value={assignee}
                disabled={busy}
                onChange={(event) => setAssignee(event.target.value)}
              >
                <option value="">未指派</option>
                {collaborators.map((member) => (
                  <option key={member.memberId} value={member.memberId}>{member.label}</option>
                ))}
              </select>
            </label>
            <button type="submit" disabled={busy}>{busy ? '指派中…' : '更新指派'}</button>
          </form>
        ) : null}

        {confirmResolve ? (
          <div className="journey-situation__confirm">
            <p>確定將這個旅程狀況標記為已處理嗎？</p>
            <label htmlFor={resolveNoteId}>
              resolutionNote（選填）
              <textarea
                id={resolveNoteId}
                maxLength={500}
                value={resolutionNote}
                disabled={busy}
                onChange={(event) => setResolutionNote(event.target.value)}
              />
            </label>
            <div className="journey-situation__confirm-actions">
              <button type="button" disabled={busy} onClick={() => setConfirmResolve(false)}>返回</button>
              <button
                type="button"
                className="journey-situation__primary"
                disabled={busy}
                onClick={() => runMutation(() => journeySituationApi.resolve(
                  journey.journeyId,
                  situation.situationId,
                  buildResolveSituationBody(resolutionNote, situation.rowVersion),
                )).then((ok) => { if (ok) setConfirmResolve(false); })}
              >
                {busy ? '處理中…' : '確認已處理'}
              </button>
            </div>
          </div>
        ) : null}

        {confirmCancel ? (
          <div className="journey-situation__confirm">
            <p>確定將這個旅程狀況標記為 CANCELLED 嗎？這不是刪除。</p>
            <label htmlFor={cancelReasonId}>
              reason
              <textarea
                id={cancelReasonId}
                maxLength={500}
                value={cancelReason}
                disabled={busy}
                onChange={(event) => setCancelReason(event.target.value)}
              />
            </label>
            <div className="journey-situation__confirm-actions">
              <button type="button" disabled={busy} onClick={() => setConfirmCancel(false)}>返回</button>
              <button
                type="button"
                className="journey-situation__danger"
                disabled={busy || !String(cancelReason).trim()}
                onClick={() => runMutation(() => journeySituationApi.cancel(
                  journey.journeyId,
                  situation.situationId,
                  buildCancelSituationBody(cancelReason, situation.rowVersion),
                )).then((ok) => { if (ok) setConfirmCancel(false); })}
              >
                {busy ? '取消中…' : '確認取消狀況'}
              </button>
            </div>
          </div>
        ) : null}
      </section>
    </main>
  );
}
